/**
 * DocumentsPanel — self-contained documents tab for an expediente (Cumplimiento
 * lens). Lists the expediente's documents (api.listDocuments), lets an analyst
 * add a document (api.addDocument) and change a document's estado through a
 * ReasonModal-style dialog so the decision + motivo land in the bitácora
 * (api.updateDocument). Reuses the design-system primitives; es-PA only.
 */
import { useLayoutEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ALLOWED_DOCUMENT_MIME_TYPES } from '@cumpli/shared';
import { api } from '../lib/api';
import { formatDate, invalidateExpedienteTimeline } from '../lib/hooks';
import { useScrollMemory } from '../lib/use-scroll-memory';
import { useToast } from '../lib/use-toast';
import { DOC_CATEGORIES, type EstadoDoc, type ExpedienteDoc } from '../lib/phase1';
import { Button, Card, Dialog, EmptyState, Label, Pill, Skeleton } from './ui';
import { ReasonModal, docTone } from './compliance';
import { DocumentPreviewDialog } from './DocumentPreview';
import { FileText, Plus, Replace, RotateCcw, Trash2, Undo2 } from 'lucide-react';

// Shared <select> control class with focus styling, matching the SELECT_CLS used
// by ReviewsPanel/OwnersPanel and the rest of the design system.
const SELECT_CLS =
  'h-10 w-full rounded-lg border border-line-strong bg-canvas px-3 text-sm text-ink focus:border-primary focus:bg-panel focus:outline-none focus:ring-2 focus:ring-primary/30';

const ACCEPT = ALLOWED_DOCUMENT_MIME_TYPES.join(',');

/** Estados an analyst can transition a document to, with the motivo options. */
const ESTADO_OPTIONS: { estado: EstadoDoc; reasons: string[] }[] = [
  { estado: 'Aceptado', reasons: ['Documento legible y vigente', 'Cumple requisito de Ley 23'] },
  { estado: 'Vigente', reasons: ['Verificado contra fuente oficial', 'Dentro del período de validez'] },
  { estado: 'Bajo Revisión', reasons: ['Requiere verificación adicional', 'Pendiente de cotejo'] },
  { estado: 'Por Vencer', reasons: ['Próximo a la fecha de vencimiento', 'Solicitar renovación al cliente'] },
  { estado: 'Vencido', reasons: ['Fecha de vigencia superada', 'Reemplazo requerido'] },
  { estado: 'Solicitado', reasons: ['Documento no recibido', 'Reenviar solicitud al cliente'] },
  { estado: 'Recibido', reasons: ['Documento recibido del cliente', 'Pendiente de revisión'] },
];

// All categories an analyst can pick when attaching a document, deduped across personas.
const ALL_CATEGORIES = Array.from(
  new Set([...DOC_CATEGORIES.natural, ...DOC_CATEGORIES.legal])
);

function DocSkeleton() {
  return (
    <div className="grid gap-2.5">
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex items-center gap-3.5 rounded-2xl border border-line bg-canvas px-4 py-3.5">
          <Skeleton className="h-11 w-11 shrink-0 rounded-xl" />
          <div className="min-w-0 flex-1 space-y-2">
            <Skeleton className="h-3.5 w-40" />
            <Skeleton className="h-3 w-24" />
          </div>
          <Skeleton className="h-6 w-20 rounded-full" />
        </div>
      ))}
    </div>
  );
}

/** Dialog to upload a new document file (category + file → presigned S3 upload). */
function AddDocDialog({
  open,
  busy,
  onClose,
  onUpload,
}: {
  open: boolean;
  busy: boolean;
  onClose: () => void;
  onUpload: (input: { file: File; category: string }) => void;
}) {
  const [category, setCategory] = useState(ALL_CATEGORIES[0] ?? '');
  const [file, setFile] = useState<File | null>(null);
  const canSubmit = file !== null && category.length > 0;

  return (
    <Dialog open={open} title="Subir documento" onClose={onClose}>
      <div className="space-y-4">
        <div>
          <Label htmlFor="doc-category">Categoría</Label>
          <select
            id="doc-category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className={SELECT_CLS}
          >
            {ALL_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label htmlFor="doc-file">Archivo (PDF o imagen)</Label>
          <input
            id="doc-file"
            type="file"
            accept={ACCEPT}
            disabled={busy}
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="block w-full text-sm text-ink-soft file:mr-3 file:rounded-lg file:border-0 file:bg-side file:px-3 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-side/90"
          />
          {file && (
            <p className="mt-1.5 font-mono text-[10.5px] text-ink-faint">
              {file.name} · {(file.size / 1024).toFixed(0)} KB
            </p>
          )}
        </div>
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          <Button disabled={!canSubmit || busy} onClick={() => file && onUpload({ file, category })}>
            {busy ? 'Subiendo…' : 'Subir'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

/**
 * Dialog to replace the file of an EXISTING document. Same file-picker pattern as
 * AddDocDialog but without the category field — replace keeps the doc's category
 * and estado, only swapping the attached file.
 */
function ReplaceDocDialog({
  docName,
  busy,
  onClose,
  onReplace,
}: {
  docName: string;
  busy: boolean;
  onClose: () => void;
  onReplace: (file: File) => void;
}) {
  const [file, setFile] = useState<File | null>(null);

  return (
    <Dialog open title="Reemplazar documento" onClose={onClose}>
      <div className="space-y-4">
        <p className="text-sm text-ink-soft">
          Sube un archivo nuevo para <b className="text-ink">{docName}</b>. El anterior se
          reemplaza; la categoría y el estado se mantienen.
        </p>
        <div>
          <Label htmlFor="replace-file">Archivo (PDF o imagen)</Label>
          <input
            id="replace-file"
            type="file"
            accept={ACCEPT}
            disabled={busy}
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="block w-full text-sm text-ink-soft file:mr-3 file:rounded-lg file:border-0 file:bg-side file:px-3 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-side/90"
          />
          {file && (
            <p className="mt-1.5 font-mono text-[10.5px] text-ink-faint">
              {file.name} · {(file.size / 1024).toFixed(0)} KB
            </p>
          )}
        </div>
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          <Button disabled={!file || busy} onClick={() => file && onReplace(file)}>
            {busy ? 'Reemplazando…' : 'Reemplazar'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

export default function DocumentsPanel({
  expedienteId,
  ownerId,
}: {
  expedienteId: string;
  // Fase 4 drill-down (req#7): when set, the panel lists ONLY this person's
  // documents (and scopes uploads to them). Absent = every document of the
  // expediente, with per-person docs labelled "de {persona}".
  ownerId?: string;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const scrollMemory = useScrollMemory();
  const [addOpen, setAddOpen] = useState(false);
  // When on, the list shows the soft-deleted (papelera) documents instead of the active ones.
  const [showDeleted, setShowDeleted] = useState(false);
  // The document whose estado is being changed (drives the ReasonModal).
  const [editing, setEditing] = useState<{ doc: ExpedienteDoc; estado: EstadoDoc } | null>(null);
  // The document being previewed (drives DocumentPreviewDialog).
  const [previewing, setPreviewing] = useState<ExpedienteDoc | null>(null);
  // The document pending soft-delete (drives the confirm Dialog).
  const [deleting, setDeleting] = useState<ExpedienteDoc | null>(null);
  // The document whose file is being replaced (drives ReplaceDocDialog).
  const [replacing, setReplacing] = useState<ExpedienteDoc | null>(null);

  // All variants share the `['expedientes', id, 'documents']` prefix so a single
  // prefix-invalidation refreshes the active, deleted and per-person lists.
  const docsKey = [
    'expedientes',
    expedienteId,
    'documents',
    ...(ownerId ? ['owner', ownerId] : []),
    ...(showDeleted ? ['deleted'] : []),
  ];

  const {
    data: docs,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: docsKey,
    queryFn: () =>
      api.listDocuments(expedienteId, {
        ...(showDeleted ? { deleted: true } : {}),
        ...(ownerId ? { ownerId } : {}),
      }),
  });

  // Only the entity view (no ownerId) labels per-person docs, so resolve owner
  // names lazily and only there.
  const { data: owners } = useQuery({
    queryKey: ['expedientes', expedienteId, 'owners'],
    queryFn: () => api.listOwners(expedienteId),
    enabled: !ownerId,
  });
  const ownerName = (oid: string) => owners?.find((o) => o.id === oid)?.name ?? 'persona';

  // Restore the scroll position saved for this view once its content commits, so
  // toggling between the (often short) papelera and the active list lands the
  // user back where they were rather than clamped near the top.
  useLayoutEffect(() => {
    if (!isLoading) scrollMemory.restore(showDeleted ? 'deleted' : 'active');
  }, [showDeleted, isLoading]);

  // Prefix-match invalidation covers the active list AND the deleted list.
  // ALSO invalidates the unified Actividad timeline (bug fix: a document
  // upload/estado-change/replace/delete/restore must show up there live, not
  // just after a reload — see `invalidateExpedienteTimeline`).
  const invalidate = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ['expedientes', expedienteId, 'documents'] }),
      invalidateExpedienteTimeline(qc, expedienteId),
    ]);

  const uploadMutation = useMutation({
    mutationFn: (input: { file: File; category: string }) =>
      api.uploadDocument(expedienteId, ownerId ? { ...input, ownerId } : input),
    onSuccess: async (_data, input) => {
      await invalidate();
      setAddOpen(false);
      toast({ message: `Documento subido — ${input.file.name}`, kind: 'success' });
    },
    onError: (err) =>
      toast({
        message: err instanceof Error ? err.message : 'No se pudo subir el documento',
        kind: 'error',
      }),
  });

  const estadoMutation = useMutation({
    mutationFn: ({ doc, estado }: { doc: ExpedienteDoc; estado: EstadoDoc; reason: string }) =>
      api.updateDocument(expedienteId, doc.id, { estado }),
    onSuccess: async (_data, vars) => {
      await invalidate();
      setEditing(null);
      toast({ message: `Estado actualizado — ${vars.estado}`, kind: 'success' });
    },
    onError: () => toast({ message: 'No se pudo actualizar el estado', kind: 'error' }),
  });

  const replaceMutation = useMutation({
    mutationFn: ({ doc, file }: { doc: ExpedienteDoc; file: File }) =>
      api.replaceDocument(expedienteId, doc.id, file),
    onSuccess: async (_data, vars) => {
      await invalidate();
      setReplacing(null);
      toast({ message: `Documento reemplazado — ${vars.file.name}`, kind: 'success' });
    },
    onError: (err) =>
      toast({
        message: err instanceof Error ? err.message : 'No se pudo reemplazar el documento',
        kind: 'error',
      }),
  });

  const deleteMutation = useMutation({
    mutationFn: (doc: ExpedienteDoc) => api.deleteDocument(expedienteId, doc.id),
    onSuccess: async () => {
      await invalidate();
      setDeleting(null);
      toast({ message: 'Documento enviado a la papelera', kind: 'success' });
    },
    onError: (err) =>
      toast({
        message: err instanceof Error ? err.message : 'No se pudo eliminar el documento',
        kind: 'error',
      }),
  });

  const restoreMutation = useMutation({
    mutationFn: (doc: ExpedienteDoc) => api.restoreDocument(expedienteId, doc.id),
    onSuccess: async () => {
      await invalidate();
      toast({ message: 'Documento restaurado', kind: 'success' });
    },
    onError: (err) =>
      toast({
        message: err instanceof Error ? err.message : 'No se pudo restaurar el documento',
        kind: 'error',
      }),
  });

  return (
    <Card>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-serif text-xl font-medium text-ink">Documentos</h2>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant={showDeleted ? 'default' : 'ghost'}
            onClick={() => {
              scrollMemory.remember(showDeleted ? 'deleted' : 'active');
              setShowDeleted((v) => !v);
            }}
          >
            <Trash2 className="h-4 w-4" strokeWidth={2} />{' '}
            {showDeleted ? 'Ver activos' : 'Mostrar eliminados'}
          </Button>
          {!showDeleted && (
            <Button
              variant="outline"
              onClick={() => setAddOpen(true)}
              disabled={uploadMutation.isPending}
            >
              <Plus className="h-4 w-4" strokeWidth={2.5} /> Subir
            </Button>
          )}
        </div>
      </div>

      {isLoading ? (
        <DocSkeleton />
      ) : isError ? (
        <EmptyState
          icon={<FileText className="h-8 w-8" strokeWidth={1.5} />}
          title="No se pudieron cargar los documentos"
          hint={error instanceof Error ? error.message : 'Intenta de nuevo en unos segundos.'}
          action={
            <Button variant="outline" onClick={() => void refetch()}>
              <RotateCcw className="h-4 w-4" strokeWidth={2} /> Reintentar
            </Button>
          }
        />
      ) : !docs || docs.length === 0 ? (
        showDeleted ? (
          <EmptyState
            icon={<Trash2 className="h-8 w-8" strokeWidth={1.5} />}
            title="No hay documentos eliminados"
            hint="Los documentos que envíes a la papelera aparecerán aquí para restaurarlos."
          />
        ) : (
          <EmptyState
            icon={<FileText className="h-8 w-8" strokeWidth={1.5} />}
            title="Aún no hay documentos"
            hint="Sube el primer documento del expediente para comenzar la revisión."
            action={
              <Button variant="outline" onClick={() => setAddOpen(true)}>
                <Plus className="h-4 w-4" strokeWidth={2.5} /> Subir documento
              </Button>
            }
          />
        )
      ) : (
        <div className="grid gap-2.5 [&>*]:min-w-0">
          {docs.map((d) => (
            <div
              key={d.id}
              className="flex flex-wrap items-center gap-3.5 rounded-2xl border border-line bg-canvas px-4 py-3.5"
            >
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-panel text-ink-soft">
                <FileText className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="truncate text-sm font-bold text-ink">{d.name}</span>
                  {!ownerId && d.ownerId && (
                    <span className="inline-flex shrink-0 items-center rounded-full bg-tone-slate-bg px-2 py-0.5 font-mono text-[10px] font-medium text-tone-slate">
                      de {ownerName(d.ownerId)}
                    </span>
                  )}
                </div>
                <div className="truncate font-mono text-[10.5px] text-ink-faint">
                  {d.category}
                  {d.expiry ? ` · vence ${formatDate(d.expiry, '—')}` : ''}
                </div>
              </div>
              <Pill tone={docTone(d.estado)} className="shrink-0">
                {d.estado}
              </Pill>
              {showDeleted ? (
                <Button
                  variant="outline"
                  className="shrink-0"
                  disabled={restoreMutation.isPending}
                  onClick={() => restoreMutation.mutate(d)}
                >
                  <Undo2 className="h-4 w-4" strokeWidth={2} /> Restaurar
                </Button>
              ) : (
                <>
                  <Button variant="ghost" className="shrink-0" onClick={() => setPreviewing(d)}>
                    Ver
                  </Button>
                  <Button
                    variant="ghost"
                    className="shrink-0"
                    disabled={estadoMutation.isPending}
                    onClick={() => setEditing({ doc: d, estado: d.estado })}
                  >
                    Cambiar estado
                  </Button>
                  <Button
                    variant="ghost"
                    className="shrink-0"
                    disabled={replaceMutation.isPending}
                    onClick={() => setReplacing(d)}
                  >
                    <Replace className="h-4 w-4" strokeWidth={2} /> Reemplazar
                  </Button>
                  <Button
                    variant="ghost"
                    className="shrink-0 text-tone-red hover:bg-tone-red/10"
                    disabled={deleteMutation.isPending}
                    onClick={() => setDeleting(d)}
                  >
                    <Trash2 className="h-4 w-4" strokeWidth={2} /> Eliminar
                  </Button>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {previewing && (
        <DocumentPreviewDialog
          title={previewing.name}
          cacheKey={`${expedienteId}:${previewing.id}`}
          fetchUrl={() => api.getDocumentDownloadUrl(expedienteId, previewing.id)}
          onClose={() => setPreviewing(null)}
        />
      )}

      <AddDocDialog
        open={addOpen}
        busy={uploadMutation.isPending}
        onClose={() => setAddOpen(false)}
        onUpload={(input) => uploadMutation.mutate(input)}
      />

      {editing && (
        <EstadoModal
          doc={editing.doc}
          busy={estadoMutation.isPending}
          onClose={() => setEditing(null)}
          onConfirm={(estado, reason) =>
            estadoMutation.mutate({ doc: editing.doc, estado, reason })
          }
        />
      )}

      {replacing && (
        <ReplaceDocDialog
          docName={replacing.name}
          busy={replaceMutation.isPending}
          onClose={() => setReplacing(null)}
          onReplace={(file) => replaceMutation.mutate({ doc: replacing, file })}
        />
      )}

      {deleting && (
        <Dialog open title="Eliminar documento" onClose={() => setDeleting(null)}>
          <p className="text-sm text-ink-soft">
            El documento <b className="text-ink">{deleting.name}</b> se moverá a la papelera.
            Podrás restaurarlo con <b className="text-ink">Mostrar eliminados</b> mientras siga
            ahí.
          </p>
          <div className="mt-6 flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => setDeleting(null)}
              disabled={deleteMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteMutation.mutate(deleting)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Eliminando…' : 'Eliminar'}
            </Button>
          </div>
        </Dialog>
      )}
    </Card>
  );
}

/**
 * Estado-change flow: pick the target estado, then confirm with a required
 * motivo (ReasonModal). The motivo is captured for the bitácora; the backend
 * patch only carries the new estado.
 */
function EstadoModal({
  doc,
  busy,
  onClose,
  onConfirm,
}: {
  doc: ExpedienteDoc;
  busy: boolean;
  onClose: () => void;
  onConfirm: (estado: EstadoDoc, reason: string) => void;
}) {
  // Default to the first estado that isn't the document's current one.
  const [target, setTarget] = useState<EstadoDoc>(
    () => ESTADO_OPTIONS.find((o) => o.estado !== doc.estado)?.estado ?? ESTADO_OPTIONS[0]!.estado
  );
  const reasons = ESTADO_OPTIONS.find((o) => o.estado === target)?.reasons ?? [
    'Actualización de estado',
  ];

  return (
    <ReasonModal
      open
      title={`Cambiar estado — ${doc.name}`}
      confirmLabel="Guardar estado"
      reasons={reasons}
      extra={
        <div>
          <Label htmlFor="doc-estado">Nuevo estado</Label>
          <select
            id="doc-estado"
            value={target}
            onChange={(e) => setTarget(e.target.value as EstadoDoc)}
            disabled={busy}
            className={SELECT_CLS}
          >
            {ESTADO_OPTIONS.map((o) => (
              <option key={o.estado} value={o.estado}>
                {o.estado}
                {o.estado === doc.estado ? ' (actual)' : ''}
              </option>
            ))}
          </select>
        </div>
      }
      onClose={onClose}
      onConfirm={(reason) => onConfirm(target, reason)}
    />
  );
}
